import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Container,
    Tab,
    Tabs,
    Grid,
    Grid2,
    Tooltip,
    Avatar,
    Button,
    Snackbar,
    Alert,
} from "@mui/material";
import "./TalentDashboard.css";
import SearchInput from "../../components/SearchInput";
import ProfileDashboard from "./profileDashboard/ProfileDashboard";
import Engagement from "./engagement/Engagement";
import Audience from "./audience/Audience";
import Posts from "./posts/Posts";
import Mentions from "./mentions/Mentions";
import useSWR from "swr";
import API_ENDPOINTS from "../../apis/endpoints";
import useSWRMutation from "swr/mutation";
import { Card } from "../../components/Card";
import { styled } from "@mui/styles";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const StyledTab = styled(Tab)(({ theme }) => ({
    textTransform: "none",
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.typography.pxToRem(15),
    marginRight: theme.spacing(1),
    "&.Mui-selected": {
        color: "#000",
    },
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
    borderBottom: `1px solid ${theme.palette.neutral[200]}`,
    marginTop: "20px",
    "& .MuiTabs-indicator": {
        backgroundColor: "theme.palette.secondary[500]",
    },
}));

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

const tabs = [
    { id: 0, title: "Instagram" },
    { id: 1, title: "YouTube" },
];

// Helper function to get date string for filename
const getDateString = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

function TalentDashboard() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const dashboardRef = useRef(null);
    const profileRef = useRef(null);
    const tabsRef = useRef(null);
    const { search } = useLocation();
    // Add this line to define originalTabState as a ref
    const originalTabState = useRef({ platformTab: 0, contentTab: 0 });

    // State for export functionality
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const platform = ["instagram", "youtube"].includes(params.get("platform"))
        ? params.get("platform")
        : "instagram";

    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState(searchTerm);
    const [activeTab, setActiveTab] = useState(0);

    // MOVE THIS BEFORE THE USEEFFECT
    const { data, error, isMutating, trigger, reset } = useSWRMutation(
        searchTerm
            ? `${API_ENDPOINTS.ENGAGEMENT_INSIGHT}${platform}/?handle=${searchTerm}`
            : null
    );

    // Modified effect to reset state when navigating to the dashboard
    useEffect(() => {
        // Reset search state and platform when component mounts or is navigated to
        setSearchTerm("");
        setSearchQuery("");
        setActiveTab(0);

        // Set platform to instagram by default when directly navigating to the dashboard
        if (!params.get("platform")) {
            navigate("?platform=instagram", { replace: true, relative: true });
        }

        // Reset any cached data
        reset();

        // Clear any previous data
        if (data) {
            reset();
        }
    }, [navigate, reset, params]);
    const {
        data: historyData,
        error: historyError,
        isLoading,
    } = useSWR(`${API_ENDPOINTS.ENGAGEMENT_INSIGHT_HISTORY}`);
    useEffect(() => {
        // Check if reset parameter is present
        if (search === "") {
            setSearchTerm("");
            setSearchQuery("");
            setActiveTab(0); // Add this line
            reset(); // Clears the fetched data from useSWRMutation
        }
        if (params.get("reset") === "true") {
            // Reset all state
            setSearchTerm("");
            setSearchQuery("");
            reset();
            setActiveTab(0);

            // Remove the reset parameter from URL to prevent repeated resets
            navigate("?platform=instagram", { replace: true, relative: true });
        }
    }, [params, navigate, reset, search]);

    const handleSearch = useCallback(
        (term) => {
            term = term?.trim();
            if (!term) {
                setSearchTerm("");
                setSearchQuery("");
                setActiveTab(0); // Add this line
                reset();
                return;
            }

            setSearchTerm(term);
            setActiveTab(0); // Add this line to reset to first tab when searching
            setTimeout(() => {
                trigger();
            }, 0);
        },
        [reset, trigger]
    );

    const displayData = data && Object.keys(data)?.length > 0 ? data : null;

    const handleChange = useCallback(
        (event, newValue) => {
            setSearchTerm("");
            setSearchQuery("");
            setActiveTab(0); // Add this line
            reset();
            // Reset activeTab to 0 when switching platforms
            setActiveTab(0);
            navigate(`?platform=${tabs[newValue].title.toLowerCase()}`, {
                replace: true,
                relative: true,
            });
        },
        [navigate, reset]
    );

    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue);
    }, []);

    // Get tab components based on platform
    const getTabComponents = useCallback(() => {
        const isYouTube = platform === "youtube";
        if (isYouTube) {
            return [
                {
                    title: "Engagement",
                    component: Engagement,
                    key: "engagement",
                },
                {
                    title: "Audience",
                    component: Audience,
                    key: "audience",
                },
                {
                    title: "Content",
                    component: Posts,
                    key: "posts",
                },
            ];
        } else {
            return [
                {
                    title: "Engagement",
                    component: Engagement,
                    key: "engagement",
                },
                {
                    title: "Audience",
                    component: Audience,
                    key: "audience",
                },
                {
                    title: "Content",
                    component: Posts,
                    key: "posts",
                },
                {
                    title: "Mentions",
                    component: Mentions,
                    key: "mentions",
                },
            ];
        }
    }, [platform]);

    // Function to capture a DOM element and add it to the PDF
    // Optimized PDF generation function with size reduction
    const captureAndAddToPage = useCallback(async (element, title, pdf) => {
        if (!element) {
            console.warn(`Element for ${title} not found`);
            return false;
        }

        try {
            // Add a new page for each component (except the first one)
            if (title !== "Profile Information") {
                pdf.addPage();
            }

            // Add section title
            pdf.setFontSize(18);
            pdf.setTextColor(85, 85, 85);
            pdf.text(title, 10, 20);

            // Log initial state

            // OPTIMIZATION 1: Reduce scale for better file size
            const scale = title === "Mentions" ? 1.2 : 1.5;

            // OPTIMIZATION 2: Use better compression options
            const canvas = await html2canvas(element, {
                scale: scale, // Reduced scale for better file size
                logging: false,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#FFFFFF", // Ensure white background
                ignoreElements: (element) => {
                    // OPTIMIZATION 3: Ignore more elements for mentions tab
                    if (
                        title === "Mentions" &&
                        (element.classList.contains("no-export") ||
                            element.classList.contains("MuiSkeleton-root"))
                    ) {
                        return true;
                    }
                    return element.classList.contains("no-export");
                },
            });

            // OPTIMIZATION 4: Compress image quality for PDF
            const imgQuality = title === "Mentions" ? 0.7 : 0.85;

            // Convert to image with reduced quality for mentions
            const imgData = canvas.toDataURL("image/jpeg", imgQuality);

            // Calculate original image size
            const originalSize = imgData.length;

            // Calculate dimensions to fit on PDF
            const imgWidth = 190; // A4 width minus margins
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // OPTIMIZATION 5: Split mentions content across pages if too large
            if (title === "Mentions" && imgHeight > 240) {
                console.log(
                    `Mentions content is large (${imgHeight}mm height), splitting across pages`
                );

                // First page with header
                pdf.addImage(
                    imgData,
                    "JPEG",
                    10,
                    30,
                    imgWidth,
                    Math.min(imgHeight, 240)
                );

                // If content is larger than one page, continue on next page
                if (imgHeight > 240) {
                    const remainingHeight = imgHeight - 240;
                    pdf.addPage();
                    pdf.setFontSize(18);
                    pdf.setTextColor(85, 85, 85);
                    pdf.text(`${title} (continued)`, 10, 20);

                    // Use clipping to show the remaining portion
                    pdf.addImage(
                        imgData,
                        "JPEG",
                        10,
                        30 - 240, // Adjust y-position to show the remaining content
                        imgWidth,
                        imgHeight
                    );
                    console.log(
                        `Added second page for Mentions with remaining ${remainingHeight}mm height`
                    );
                }
            } else {
                // Add image to PDF - positioned below the heading
                pdf.addImage(imgData, "JPEG", 10, 30, imgWidth, imgHeight);
            }

            return true;
        } catch (error) {
            console.error(`Error capturing ${title}:`, error);
            return false;
        }
    }, []);

    // Function to add profile section to PDF
    // Function to add profile section to PDF with optimization
    const addProfileSection = useCallback(
        async (pdf) => {
            const element = profileRef.current;
            if (!element || !displayData) return false;

            try {
                const profile = displayData.profile;

                // Add section header text
                pdf.setFontSize(22);
                pdf.setTextColor(0, 0, 0);
                pdf.text(
                    `Talent Report: ${profile?.name || profile?.username || ""}`,
                    10,
                    20
                );

                pdf.setFontSize(12);
                pdf.setTextColor(100, 100, 100);
                pdf.text(
                    `Platform: ${platform.charAt(0).toUpperCase() + platform.slice(1)}`,
                    10,
                    30
                );
                pdf.text(
                    `Generated On: ${new Date().toLocaleDateString()}`,
                    10,
                    40
                );
                pdf.line(10, 45, 200 - 10, 45);

                // Delay to ensure layout is complete
                await new Promise((resolve) => requestAnimationFrame(resolve));
                await new Promise((resolve) => setTimeout(resolve, 100));

                // OPTIMIZATION: Use lower scale and compression for profile

                const canvas = await html2canvas(element, {
                    scale: 1.3, // Reduced scale for better file size
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    backgroundColor: "#FFFFFF",
                    ignoreElements: (el) => el.classList.contains("no-export"),
                });

                // Use better compression for profile image
                const imgData = canvas.toDataURL("image/jpeg", 0.9);
                const originalSize = imgData.length;

                const imgWidth = 190;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                pdf.addImage(imgData, "JPEG", 10, 50, imgWidth, imgHeight);

                return true;
            } catch (error) {
                console.error("Error capturing ProfileDashboard:", error);
                return false;
            }
        },
        [displayData, platform]
    );

    // Main PDF generation function
    const generateComprehensivePDF = useCallback(async () => {
        if (!displayData) return false;

        setIsExporting(true);
        setExportProgress(0);

        try {
            // Store original tab states
            const platformTabsElement = document.querySelector(
                '[aria-label="Platform tabs"]'
            );
            const contentTabsElement = document.querySelector(
                '[aria-label="Content tabs"]'
            );

            if (platformTabsElement) {
                originalTabState.current.platformTab = Array.from(
                    platformTabsElement.children
                ).findIndex(
                    (tab) => tab.getAttribute("aria-selected") === "true"
                );
            }

            if (contentTabsElement) {
                originalTabState.current.contentTab = Array.from(
                    contentTabsElement.children
                ).findIndex(
                    (tab) => tab.getAttribute("aria-selected") === "true"
                );
            }

            // Create PDF document
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: true, // Enable PDF compression
                putOnlyUsedFonts: true, // Only include used fonts
                precision: 2, // Reduce precision for smaller file size
            });

            // Add cover page with basic info

            // Add profile page directly to the first page (remove the pdf.addPage() call)
            setExportProgress(10);
            // pdf.addPage(); <- REMOVE THIS LINE
            await addProfileSection(pdf);

            setExportProgress(30);

            // Get tab components
            const tabComponents = getTabComponents();

            // For each tab, we need to:
            // 1. Click on the tab to make it active
            // 2. Wait for content to render
            // 3. Capture the content on a new page
            // In the generateComprehensivePDF function, add special handling for Mentions
            for (let i = 0; i < tabComponents.length; i++) {
                const tabInfo = tabComponents[i];

                // Find and click the tab
                const tabElement = document.querySelector(
                    `[aria-label="Content tabs"] [aria-controls="simple-tabpanel-${i}"]`
                );

                if (tabElement) {
                    tabElement.click();

                    // Extra wait time for Mentions tab
                    const waitTime = tabInfo.title === "Mentions" ? 1000 : 500;
                    console.log(
                        `Waiting ${waitTime}ms for ${tabInfo.title} content to render`
                    );

                    // Wait for content to render
                    await new Promise((resolve) =>
                        setTimeout(resolve, waitTime)
                    );

                    // Find the tab content
                    const tabContent = document.querySelector(
                        `#simple-tabpanel-${i} .export-section`
                    );

                    if (tabContent) {
                        // For Mentions tab, ensure all images are loaded
                        if (tabInfo.title === "Mentions") {
                            // Wait for all images to load
                            const images = tabContent.querySelectorAll("img");
                            if (images.length > 0) {
                                // Create an array of promises for each image load
                                const imagePromises = Array.from(images).map(
                                    (img) => {
                                        if (img.complete)
                                            return Promise.resolve();
                                        return new Promise((resolve) => {
                                            img.onload = resolve;
                                            img.onerror = resolve; // Handle error case too
                                        });
                                    }
                                );

                                // Wait for all images to load
                                await Promise.all(imagePromises);
                            }
                        }

                        // Capture and add to PDF on a new page
                        await captureAndAddToPage(
                            tabContent,
                            tabInfo.title,
                            pdf
                        );
                    } else {
                        console.warn(
                            `Content for ${tabInfo.title} tab not found`
                        );
                    }
                }

                setExportProgress(30 + ((i + 1) / tabComponents.length) * 60);
            }

            // Restore original tab states
            const platformTabs = document.querySelector(
                '[aria-label="Platform tabs"]'
            );
            const contentTabs = document.querySelector(
                '[aria-label="Content tabs"]'
            );

            if (
                platformTabs &&
                platformTabs.children[originalTabState.current.platformTab]
            ) {
                platformTabs.children[
                    originalTabState.current.platformTab
                ].click();
            }

            if (
                contentTabs &&
                contentTabs.children[originalTabState.current.contentTab]
            ) {
                contentTabs.children[
                    originalTabState.current.contentTab
                ].click();
            }

            setExportProgress(95);

            // Save the PDF
            const filename = `${displayData?.profile?.username || "talent"}_report_${getDateString()}.pdf`;
            pdf.save(filename);

            setExportProgress(100);
            return true;
        } catch (error) {
            console.error("Error generating PDF:", error);
            return false;
        } finally {
            setIsExporting(false);
        }
    }, [
        addProfileSection,
        captureAndAddToPage,
        displayData,
        getTabComponents,
        platform,
    ]);

    // Handle export button click
    const handleExport = useCallback(async () => {
        if (!displayData) return;

        try {
            const success = await generateComprehensivePDF();

            if (success) {
                setNotification({
                    open: true,
                    message: "PDF exported successfully!",
                    severity: "success",
                });
            } else {
                throw new Error("Export failed");
            }
        } catch (error) {
            console.error("Export error:", error);
            setNotification({
                open: true,
                message: "Failed to export PDF. Please try again.",
                severity: "error",
            });
        }
    }, [displayData, generateComprehensivePDF]);

    const closeNotification = useCallback(() => {
        setNotification((prev) => ({ ...prev, open: false }));
    }, []);

    // Get current tab component
    const tabComponents = getTabComponents();

    return (
        <Container style={{ padding: 0, width: "100%" }} ref={dashboardRef}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography variant="featureAccent" gutterBottom={true}>
                    Talent Dashboard
                </Typography>

                {/* Add Export button when data is available */}
                {!isMutating && displayData && (
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={
                            isExporting ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <FileDownloadIcon />
                            )
                        }
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? "Exporting..." : "Export PDF"}
                    </Button>
                )}
            </Box>

            <StyledTabs
                value={tabs.findIndex(
                    (tab) => tab.title.toLowerCase() === platform
                )}
                onChange={handleChange}
                aria-label="Platform tabs"
            >
                {tabs.map((tab, index) => (
                    <StyledTab
                        key={tab.id}
                        label={tab.title}
                        {...a11yProps(index)}
                    />
                ))}
            </StyledTabs>

            <Box className="talent-dashboard">
                <Box className="search-row" sx={{ p: 0 }}>
                    <SearchInput
                        searchTerm={searchQuery}
                        setSearchTerm={setSearchQuery}
                        onSearch={handleSearch}
                        sx={{ width: "80vw" }}
                        placeHolder={`Talent on ${platform}...`}
                    />
                </Box>

                {(isMutating || isLoading) && (
                    <div className="circular-loader">
                        <CircularProgress />
                    </div>
                )}

                {(error || historyError) && (
                    <Typography color="error">
                        Error: {error?.message || historyError?.message}
                    </Typography>
                )}

                {!isLoading && !isMutating && !displayData && historyData && (
                    <Box width="100%" pt={4}>
                        <Typography variant="h6">Recent Searches</Typography>
                        <Box
                            width="100%"
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "20px",
                            }}
                        >
                            {historyData.history
                                .filter((item) => item.platform === platform)
                                .map((data) => (
                                    <div
                                        key={
                                            data.id ||
                                            `${data.username}-${data.platform}`
                                        }
                                        onClick={() => {
                                            setSearchTerm(data.username);
                                            setSearchQuery(data.username);
                                            handleSearch(data.username);
                                        }}
                                    >
                                        <Card>
                                            <Grid2
                                                container
                                                spacing={3}
                                                alignItems="center"
                                            >
                                                <Grid2 item>
                                                    <Avatar
                                                        alt={data.name || ""}
                                                        src={
                                                            data.image_url || ""
                                                        }
                                                        sx={{
                                                            width: 80,
                                                            height: 80,
                                                        }}
                                                    />
                                                </Grid2>
                                                <Grid2 item xs>
                                                    <Typography variant="h6">
                                                        {data.name || ""}
                                                        {data.is_verified && (
                                                            <Tooltip title="Verified Account">
                                                                <VerifiedIcon
                                                                    color="secondary"
                                                                    sx={{
                                                                        marginLeft: 1,
                                                                        verticalAlign:
                                                                            "middle",
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="supportRegular"
                                                        color="text.secondary"
                                                    >
                                                        @{data.username || ""}{" "}
                                                        {data.location
                                                            ? `| ${data.location}`
                                                            : ""}
                                                    </Typography>
                                                </Grid2>
                                            </Grid2>
                                        </Card>
                                    </div>
                                ))}
                        </Box>
                    </Box>
                )}

                {!isMutating && displayData && (
                    <>
                        {/* Profile section - always visible */}
                        {/* <div ref={profileRef}> */}
                        <Card
                            className="export-section profile-section"
                            aria-label="Profile content"
                            ref={profileRef}
                        >
                            <ProfileDashboard
                                profileData={displayData}
                                platform={platform}
                                showTabs={false} // Make sure tabs are disabled in profile dashboard
                            />
                        </Card>
                        {/* </div> */}

                        {/* Content tabs - ONLY render these tabs once */}
                        <StyledTabs
                            value={activeTab}
                            onChange={handleTabChange}
                            aria-label="Content tabs"
                            ref={tabsRef}
                        >
                            {tabComponents.map((tab, index) => (
                                <StyledTab
                                    key={index}
                                    label={tab.title}
                                    {...a11yProps(index)}
                                />
                            ))}
                        </StyledTabs>

                        {/* Tab panels - render only the active one */}
                        {tabComponents.map((tab, index) => (
                            <div
                                key={index}
                                role="tabpanel"
                                hidden={activeTab !== index}
                                id={`simple-tabpanel-${index}`}
                                aria-labelledby={`simple-tab-${index}`}
                            >
                                {activeTab === index && tab.component && (
                                    <div className="export-section">
                                        <tab.component
                                            data={displayData}
                                            platform={platform}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </>
                )}
            </Box>

            {/* Export Progress Overlay */}
            {isExporting && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress
                        variant="determinate"
                        value={exportProgress}
                        size={70}
                        thickness={4}
                    />
                    <Typography variant="h6" sx={{ mt: 2 }}>
                        Generating PDF... {Math.round(exportProgress)}%
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Please don't close this window
                    </Typography>
                </Box>
            )}

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: "100%" }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default TalentDashboard;
